// ── Factory Functions ────────────────────────────────────────

import { SolEstimatePricingMode } from '../../enum';
import { EstimateRowModel, EstimateRowExtendedModelSubContractor } from './estimate-row.model';
import { EstimateTaxRateRowModel, EstimateTaxRateHeaderRowModel, EstimateTaxRateModel } from './estimate-tax-rate.model';
import { EstimateMaterialTypeModel, EstimateMaterialCostsModel } from './estimate-material-costs.model';
import { EstimateLaborCostsModel } from './estimate-labor-costs.model';
import { EstimateSubContractorCostsModel } from './estimate-subcontractor-costs.model';
import { EstimateMiscRiskTravelCostsModel } from './estimate-misc-risk-travel-costs.model';
import { EstimateSellAsGroupModel } from './estimate-sell-as.model';
import { EstimateUiModel } from './estimate-ui.model';
import { CONSTANTS } from '../../../Constants/Constants';

export function createDefaultRowModel(cost: number = 0): EstimateRowModel {
  return {
    cost,
    escalate: '',
    tdcPercentage: 0.00,
    grossMarginPercentage: CONSTANTS.DEFAULT_GM_PERCENT,
    markUpPercentage: CONSTANTS.DEFAULT_MARKUP_PERCENT,
    grossMargin: 0.00,
    sellPrice: 0.00,
  };
}

export function createEscalatedRowModel(cost: number = 0): EstimateRowModel {
  return {
    ...createDefaultRowModel(cost),
    escalate: '',
  };
}

export function createExtendedRowModel(name: string, isSellPriceLastModified: boolean, cost: number = 0): EstimateRowExtendedModelSubContractor {
  return {
    ...createEscalatedRowModel(cost),
    name,
    isSellPriceLastModified,
  };
}

export function createDefaultTaxRateRowModel(name: string, rate: number = 0): EstimateTaxRateRowModel {
  return {
    name,
    rate: rate,
    cost: 0,
    tdcPercentage: 0.00,
    sellPrice: 0.00,
  };
}

export function createDefaultTaxRateHeaderModel(description: string = ''): EstimateTaxRateHeaderRowModel {
  return {
    description,
    cost: 0,
    tdcPercentage: 0.00,
    sellPrice: 0.00,
  };
}

export function createDefaultTaxRateModel(rowNames: string[]): EstimateTaxRateModel {
  return {
    headerRow: createDefaultTaxRateHeaderModel(),
    taxRateRows: rowNames.map(name => createDefaultTaxRateRowModel(name)),
  };
}

export function createDefaultMaterialTypeModel(): EstimateMaterialTypeModel {
  return {
    headerRow: createEscalatedRowModel(),
    shippingAndHandling: createEscalatedRowModel(),
    warranty: createEscalatedRowModel(),
  };
}

export function createDefaultMaterialCostsModel(): EstimateMaterialCostsModel {
  return {
    materialCostsHeader: createDefaultRowModel(),
    siemensMaterial: createDefaultMaterialTypeModel(),
    outsideMaterial: createDefaultMaterialTypeModel(),
    mechanicalMaterial: createDefaultMaterialTypeModel(),
    electricalMaterial: createDefaultMaterialTypeModel(),
  };
}

export function createDefaultLaborCostsModel(): EstimateLaborCostsModel {
  return {
    laborCostsHeader: createDefaultRowModel(),
    projectManagement: createEscalatedRowModel(),
    engineering: createEscalatedRowModel(),
    networkEngineering: createEscalatedRowModel(),
    qualificationEngineering: createEscalatedRowModel(),
    cyberSecurityEngineering: createEscalatedRowModel(),
    energyEngineering: createEscalatedRowModel(),
    electricalEngineering: createEscalatedRowModel(),
    graphics: createEscalatedRowModel(),
    programming: createEscalatedRowModel(),
    specialist: createEscalatedRowModel(),
    digitalDeploymentSpecialist: createEscalatedRowModel(),
    mechanical: createEscalatedRowModel(),
    electrical: createEscalatedRowModel(),
  };
}

export function createDefaultSubContractorCostsModel(): EstimateSubContractorCostsModel {
  return {
    headerRow: createDefaultRowModel(),
    subContractorsCostsRow: [],
  };
}

export function createDefaultMiscRiskTravelCostsModel(): EstimateMiscRiskTravelCostsModel {
  const miscTravelRows = [
    'MISCELLANEOUS_COSTS', 'MECHANICAL_FEE', 'PRODUCTIVITY_RESERVE',
    'SUBCONTRACTOR_FEE', 'TRAVEL_EXPENSE_COSTS'
  ].map(name => createExtendedRowModel(name, false));

  const riskRows = [
    'CALCULATED_RISK_COSTS', 'PROJECT_ENTERED_RISK_COSTS'
  ].map(name => createExtendedRowModel(name, false));

  return {
    headerRow: createExtendedRowModel('MISC_RISK_TRAVEL_COSTS', false),
    rows: miscTravelRows,
    showMechanicalFee: true,
    riskCosts: {
      headerRow: createExtendedRowModel('RISK_COSTS', false),
      rows: riskRows,
      showRiskCostsAccordion: false,
    },
  };
}

export function createDefaultSellAsGroupModel(): EstimateSellAsGroupModel {
  return {
    calculated: { markUpPercentage: 0.00, grossMarginPercentage: 0.00 },
    reported: { markUpPercentage: 0.00, grossMarginPercentage: 0.00 },
    sellAsPricingMode: SolEstimatePricingMode.Line_Item,
    sellAsSellPrice: 0.00,
    turboGMPercent: 0.00,
  };
}

export function createDefaultEstimateUiModel(): EstimateUiModel {
  return {
    markUpToggle: false,
    materialCosts: createDefaultMaterialCostsModel(),
    laborCosts: createDefaultLaborCostsModel(),
    subContractorCosts: createDefaultSubContractorCostsModel(),
    miscRiskTravelCosts: createDefaultMiscRiskTravelCostsModel(),
    taxNotIncluded: createDefaultRowModel(),
    taxMaterialRate: createDefaultTaxRateModel(['MATERIAL_RATE']),
    taxMaterialLaborRate: createDefaultTaxRateModel(['MATERIAL_RATE', 'LABOR_RATE']),
    technologyFee: createDefaultRowModel(),
    subTotals: createDefaultRowModel(),
    sellAs: createDefaultSellAsGroupModel(),
  };
}
